import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Moon, Loader2, Sparkles, Eye, ChevronDown, ChevronUp, Trash2, History, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Expandable saved dream card component
const SavedDreamCard = ({ dream, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-babylon-obsidian/50 rounded-lg border border-babylon-gold/10 overflow-hidden">
      {/* Header - Always visible, clickable to expand */}
      <div 
        className="p-3 cursor-pointer hover:bg-babylon-gold/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs text-babylon-text-dim">
                {new Date(dream.created_at).toLocaleDateString()}
              </p>
              <ChevronDown 
                size={14} 
                className={`text-babylon-gold transition-transform ${expanded ? 'rotate-180' : ''}`} 
              />
            </div>
            <p className={`text-sm text-babylon-text ${expanded ? '' : 'line-clamp-2'}`}>
              {dream.dream}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 text-babylon-text-dim hover:text-red-400 transition-colors flex-shrink-0"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      
      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3 border-t border-babylon-gold/10 pt-3">
              {/* Interpretation */}
              {dream.interpretation && (
                <div className="bg-babylon-obsidian/80 p-3 rounded-lg">
                  <h5 className="text-xs text-babylon-gold mb-2 flex items-center gap-1">
                    <Sparkles size={12} /> Interpretation
                  </h5>
                  <p className="text-sm text-babylon-text leading-relaxed whitespace-pre-wrap">
                    {dream.interpretation}
                  </p>
                </div>
              )}
              
              {/* Symbols */}
              {dream.symbols?.length > 0 && (
                <div className="bg-babylon-amethyst/10 p-3 rounded-lg">
                  <h5 className="text-xs text-babylon-amethyst mb-2">Key Symbols</h5>
                  <ul className="space-y-1">
                    {dream.symbols.map((symbol, i) => (
                      <li key={i} className="text-xs text-babylon-text flex items-start gap-2">
                        <span className="text-babylon-gold">✦</span>
                        {symbol}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Guidance */}
              {dream.guidance && (
                <div className="bg-babylon-gold/10 p-3 rounded-lg border-l-2 border-babylon-gold">
                  <h5 className="text-xs text-babylon-gold mb-1">Guidance</h5>
                  <p className="text-xs text-babylon-text">{dream.guidance}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const DreamInterpreter = () => {
  const { token } = useAuth();
  const [dream, setDream] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savedDreams, setSavedDreams] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load saved dreams when history is opened
  useEffect(() => {
    if (showHistory && token) {
      loadSavedDreams();
    }
  }, [showHistory, token]);

  const loadSavedDreams = async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/ai/dream-interpretations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedDreams(response.data.dreams || []);
    } catch (err) {
      console.error('Failed to load dreams:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const deleteDream = async (dreamId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/ai/dream-interpretations/${dreamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedDreams(prev => prev.filter(d => d.id !== dreamId));
      toast.success('Dream deleted');
    } catch (err) {
      toast.error('Failed to delete dream');
    }
  };

  const interpretDream = async () => {
    if (dream.trim().length < 20) {
      toast.error('Please describe your dream in more detail');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/ai/dream-interpretation`, 
        { dream },
        { headers }
      );
      setResult(response.data);
      setExpanded(true);
      toast.success('Dream interpreted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to interpret dream');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      className="glass-card p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="font-heading text-lg text-babylon-gold flex items-center gap-2">
          <Moon className="w-5 h-5 text-babylon-amethyst" /> Dream Interpreter
        </h3>
        <div className="flex items-center gap-2">
          {token && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2 text-babylon-text-dim hover:text-babylon-gold"
              onClick={(e) => {
                e.stopPropagation();
                setShowHistory(!showHistory);
                setExpanded(true);
              }}
            >
              <History size={16} />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            {/* Dream History */}
            {showHistory ? (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm text-babylon-gold">Saved Dreams</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs"
                    onClick={() => setShowHistory(false)}
                  >
                    New Dream
                  </Button>
                </div>
                
                {loadingHistory ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-babylon-gold" />
                  </div>
                ) : savedDreams.length === 0 ? (
                  <p className="text-sm text-babylon-text-dim text-center py-4">
                    No saved dreams yet. Interpret a dream to save it.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {savedDreams.map((d) => (
                      <SavedDreamCard 
                        key={d.id || d.created_at} 
                        dream={d} 
                        onDelete={() => deleteDream(d.id || d.created_at)} 
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <p className="text-xs text-babylon-text-dim mt-2 mb-4">
                  Describe your dream and receive an interpretation with astrological insights.
                  {token && <span className="text-babylon-gold"> Dreams are auto-saved.</span>}
                </p>

                {/* Dream Input */}
                <Textarea
                  value={dream}
                  onChange={(e) => setDream(e.target.value.slice(0, 2000))}
                  placeholder="I dreamed that I was flying over a vast ocean..."
                  className="min-h-[100px] bg-babylon-obsidian border-babylon-gold/20 mb-2"
                />
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs text-babylon-text-dim">{dream.length}/2000</span>
                  <Button 
                    onClick={interpretDream}
                    disabled={loading || dream.trim().length < 20}
                    className="btn-primary"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Interpreting...</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Interpret Dream</>
                    )}
                  </Button>
                </div>

                {/* Result */}
                {result && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4 pt-4 border-t border-babylon-gold/20"
                  >
                    {/* Auto-saved indicator */}
                    {token && result.id && (
                      <div className="flex items-center gap-2 text-xs text-green-400">
                        <Save size={12} /> Dream auto-saved
                      </div>
                    )}
                    
                    {/* Interpretation */}
                    <div className="bg-babylon-obsidian/50 p-4 rounded-lg">
                      <h4 className="text-sm text-babylon-gold mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> Interpretation
                      </h4>
                      <p className="text-sm text-babylon-text leading-relaxed whitespace-pre-wrap">
                        {result.interpretation}
                      </p>
                    </div>

                    {/* Symbols */}
                    {result.symbols?.length > 0 && (
                      <div className="bg-babylon-amethyst/10 p-4 rounded-lg">
                        <h4 className="text-sm text-babylon-amethyst mb-2">Key Symbols</h4>
                        <ul className="space-y-1">
                          {result.symbols.map((symbol, i) => (
                            <li key={i} className="text-sm text-babylon-text flex items-start gap-2">
                              <span className="text-babylon-gold">✦</span>
                              {symbol}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Guidance */}
                    {result.guidance && (
                      <div className="bg-babylon-gold/10 p-4 rounded-lg border-l-2 border-babylon-gold">
                        <h4 className="text-xs text-babylon-gold mb-1">Guidance</h4>
                        <p className="text-sm text-babylon-text">{result.guidance}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
